/**
 * Live typing presence (ticket #384): the shared contract for the
 * ephemeral "who is typing" channel — the window constants that both
 * the clients' heartbeat cadence and the fade evaluation read, the
 * liveness predicate, and the gated List read's wire shapes.
 *
 * The gate is VIEWER-SIDE and lives server-side (the mirrored-gate
 * law): every Member broadcasts, only Paid subscribers receive
 * identities, and a free caller receives latestActivityAt and NOTHING
 * else. In a free List of 2-3 Members (FREE_MEMBER_LIMIT) any extra
 * field — a name, an itemId, even a count — identifies the typist by
 * inference, which is the exact leak the one-field free payload exists
 * to prevent.
 *
 * EXPIRY IS CLIENT-EVALUATED (the Convex timing law): a query's
 * Date.now() is its deterministic read timestamp — pinned to the last
 * write on a quiet database — so a server-side wall-clock expiry can
 * never fire there. The server therefore stamps rows (mutation time,
 * which IS fresh) and projects them verbatim; every client applies
 * isPresenceLive with its own clock. A clean stop deletes the row, so
 * that path still fades instantly; a killed app fades at the window.
 */

/**
 * How long a presence row stays live without a refresh — the client
 * fade horizon and the read-side liveness predicate's window.
 */
export const PRESENCE_WINDOW_MS = 10_000;

/**
 * The client heartbeat cadence while typing. Deliberately under half
 * the window so a single dropped beat can never flicker a live chip.
 */
export const PRESENCE_HEARTBEAT_MS = 4_000;

/**
 * Is a presence stamp (a row's updatedAt, server-written) still live
 * at `now`? Live strictly less than the window. This is the CLIENT's
 * fade rule — see the module doc on why expiry cannot live in a query.
 */
export function isPresenceLive(updatedAt: number, now: number): boolean {
  return now - updatedAt < PRESENCE_WINDOW_MS;
}

/**
 * One live typist as a PAID caller receives it. itemId is absent while
 * the typist is in the Add-composer (no Item yet). The key set is
 * contract — exactly these five, nothing else (pinned by test). Rows
 * arrive UNFILTERED by freshness (one per Member per List, ≤20): the
 * client drops stale ones with isPresenceLive.
 */
export type PresencePayloadRow = {
  userId: string;
  name: string | null;
  color: string | null;
  itemId?: string;
  updatedAt: number;
};

/**
 * The gated presence read of one List. Paid callers get resolved
 * typists; free callers get the most recent activity stamp from OTHER
 * Members' rows (null = none) — one epoch-ms, no identity, no count,
 * no location. Non-members (and deleted Lists) read null, like every
 * other List-scoped query. The free client derives the tease from
 * isPresenceLive(latestActivityAt, now); the paid client filters
 * typists the same way.
 */
export type PresenceForList =
  | { typists: PresencePayloadRow[] }
  | { latestActivityAt: number | null };

/**
 * The typing reader's derived render state — the triple both platforms'
 * useListTypingPresence return. #655 converged the two hooks onto one
 * name and shape; #791 hoisted the (until then per-twin inline) return
 * annotation here so the twins cannot drift. `typists` are the live
 * rows a paid caller renders — and the mobile island shell pushes over
 * the bridge (#791's second consumer); `adders` the Add-composer's
 * ghost rows; `teaseLive` the free pill's one bit.
 */
export type ListTypingPresence = {
  typists: PresencePayloadRow[];
  adders: PresencePayloadRow[];
  teaseLive: boolean;
};

/**
 * The reader's fade re-evaluation cadence (#385): the wire keeps stale
 * rows (#384's timing law), so a client holding presence data re-runs
 * the liveness predicate on this tick. One fifth of the window (the
 * 2s tick against the 10s window) — an app-killed typist surfaces as
 * faded within the window plus at most one tick, and a clean stop
 * needs no tick at all (the row is deleted and the reactive read
 * re-renders immediately). The ratio is pinned in presencePins
 * (#536) — this prose and the constants cannot quietly diverge.
 */
export const PRESENCE_TICK_MS = 2_000;

/**
 * The free-tier tease copy (#385, fixed by the design law): appears
 * only while OTHER Members' activity is live, names nobody, and its
 * whole pill is the Settings tap target. Shared verbatim with web
 * (#386) — one set of words everywhere, like PAID_NOTES_MESSAGE.
 */
export const PRESENCE_TEASE_MESSAGE =
  "Someone is editing right now — Upgrade to see who's typing";

/**
 * "Alex is typing" — the row chip's label. A nameless Member (password
 * sign-ups can carry no name) reads as "Someone": the chip still shows
 * the dot's colour, which is identity enough for a paid viewer.
 */
export function presenceTypingLabel(name: string | null): string {
  return `${name ?? "Someone"} is typing`;
}

/** "Alex is adding an item…" — the Add-composer ghost row's label. */
export function presenceAddingLabel(name: string | null): string {
  return `${name ?? "Someone"} is adding an item…`;
}

/**
 * "Alex is editing" — what the ONE polite live region announces, STARTS
 * ONLY (announcing every stop is chatter; the disappearing chip is the
 * stop signal for sighted users and needs no spoken echo).
 */
export function presenceAnnounceEditing(name: string | null): string {
  return `${name ?? "Someone"} is editing`;
}

/**
 * The typists a PAID client renders right now: the payload's rows,
 * faded at the client's own clock. The free arm, a null read
 * (non-member), and an unsettled query all render nobody. Own typing
 * never appears here because the SERVER never sends the caller's own
 * row (#384's gate) — the client does not filter identities because it
 * never receives them.
 */
export function liveTypists(
  view: PresenceForList | null | undefined,
  now: number,
): PresencePayloadRow[] {
  if (view === null || view === undefined || !("typists" in view)) return [];
  return view.typists.filter((row) => isPresenceLive(row.updatedAt, now));
}

/**
 * The composer's ghost rows: live typists with no itemId (the
 * Add-composer has no Item to point at yet) — one ghost row each.
 */
export function composerTypists(typists: PresencePayloadRow[]): PresencePayloadRow[] {
  return typists.filter((row) => row.itemId === undefined);
}

/** The live typists riding ONE Item's row — they stack as chips. */
export function typistsOnItem(
  typists: PresencePayloadRow[],
  itemId: string,
): PresencePayloadRow[] {
  return typists.filter((row) => row.itemId === itemId);
}

/**
 * The free client's one-bit read: is anyone else's activity live? The
 * paid arm, a null read, and an unsettled query never tease — the pill
 * exists to sell the identity the free payload structurally cannot
 * carry (#384's one-field law).
 */
export function freeTeaseLive(
  view: PresenceForList | null | undefined,
  now: number,
): boolean {
  if (view === null || view === undefined || !("latestActivityAt" in view))
    return false;
  const at = view.latestActivityAt;
  return at !== null && isPresenceLive(at, now);
}

/**
 * The broadcaster's mutable view of its own typing: which surface is
 * live ("composer" or "item:<id>") and when it last beat the server.
 * `surface: null` = not typing.
 */
export type BeatState = {
  surface: string | null;
  lastBeatAt: number | null;
};

/**
 * Does this keystroke owe the server a beat? A typing START or a
 * surface SWITCH beats immediately (the switch MOVES the one row,
 * #384's upsert law — waiting for the cadence would leave the old
 * surface's chip lingering); sustained typing beats on the shared
 * PRESENCE_HEARTBEAT_MS cadence; keystrokes inside the window on the
 * same surface ride the last beat. Stopping never beats — it clears.
 */
export function beatDue(state: BeatState, surface: string, now: number): boolean {
  if (state.surface !== surface) return true;
  if (state.lastBeatAt === null) return true;
  return now - state.lastBeatAt >= PRESENCE_HEARTBEAT_MS;
}
